import { styled } from '@mui/material/styles';
import type { UseFormReturn } from 'react-hook-form';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Autocomplete, Box, Divider, TextField } from '@mui/material';
import { ExpandMoreRounded } from '@mui/icons-material';

import TextInput from '../../forms/TextInput';
import Checkbox from '../../forms/Checkbox';
import SelectMenu from '../../forms/SelectMenu';
import ChipSelectMenu from '../../forms/ChipSelectMenu';
import PhoneInput from '../../forms/PhoneInput';
import type { AlternateContactType } from '../../../data/models/alternate-contact-type.model';
import type {
  TripLegPassengerNoteType,
  TripLegDriverNoteType,
} from '../../../data/models/note.model';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as providerSlice from '../../../data/provider.slice';
import { useEffect, useMemo, useState } from 'react';
import {
  type TransportationAttribute,
  TransportationAttributeGroup,
  TransportationAttributeSubgrouping,
} from '../../../data/models/transportation-attribute.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import type { SortModel } from '../../../data/models/sort-model.model';

const PREFIX = 'MemberForm';

const classes = {
  container: `${PREFIX}-container`,
  previous: `${PREFIX}-previous`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  subctrl: `${PREFIX}-subctrl`,
  names: `${PREFIX}-names`,
  name: `${PREFIX}-name`,
  attributes: `${PREFIX}-attributes`,
  phoneNumberFields: `${PREFIX}-phoneNumberFields`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    marginLeft: theme.spacing(-2),
  },

  [`& .${classes.title}`]: {
    marginLeft: theme.spacing(-2),
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },

  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(4),
  },

  [`& .${classes.names}`]: {
    marginLeft: theme.spacing(4),
    display: 'flex',
    gap: theme.spacing(1),
  },

  [`& .${classes.name}`]: {
    flexGrow: 1,
  },

  [`& .${classes.attributes}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },

  [`& .${classes.phoneNumberFields}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 188,
    },
  },
}));

enum AttributeControlType {
  CHECKBOX,
  DROPDOWN,
}

interface SecondaryAttribute {
  id: string;
  name: string;
  controlType: AttributeControlType;
  listOptions?: TransportationAttribute[];
  displayOrder: number;
}

interface MemberFormProps {
  transportationAttributes: TransportationAttribute[];
  passengerNoteTypes: TripLegPassengerNoteType[];
  driverNoteTypes: TripLegDriverNoteType[];
  alternateContactTypes: AlternateContactType[];
  formHook: UseFormReturn<any, any>;
  isPortalReservation: boolean;
}

export default function MemberForm({
  transportationAttributes,
  passengerNoteTypes,
  driverNoteTypes,
  alternateContactTypes,
  formHook,
  isPortalReservation,
}: MemberFormProps) {
  const t = useCommonTranslation();
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();

  const showOtherInputs = formHook.watch('ShowOtherInputs');
  const showOtherAttributes = formHook.watch('ShowOtherAttributes');

  const emptyAttribute: TransportationAttribute = useMemo(
    () => ({
      transportationAttributeCategoryBC: '',
      name: t('reservation.noAttribute'),
      description: '',
      isPassengerSelectable: true,
      isDefault: false,
    }),
    [t],
  );

  const [passengerAttributes, setPassengerAttributes] = useState<
    SecondaryAttribute[]
  >([]);
  const [otherAttributes, setOtherAttributes] = useState<SecondaryAttribute[]>(
    [],
  );

  useEffect(() => {
    if (transportationAttributes) {
      const passengerAttrs: SecondaryAttribute[] = [];
      const otherAttrs: SecondaryAttribute[] = [];
      const secondary = transportationAttributes.filter(
        (a) =>
          a.grouping === TransportationAttributeGroup.SECONDARY &&
          a.isPassengerSelectable,
      );
      // Sort all of the secondary attributes into two groups: one goes under "addition passgengers"
      // and the rest go under "other attributes".
      secondary.forEach((s) => {
        let options: TransportationAttribute[] | undefined = undefined;
        if (s.childTransportationAttributeCategoryBC) {
          const children = transportationAttributes.filter(
            (ta) =>
              ta.transportationAttributeCategoryBC ===
              s.childTransportationAttributeCategoryBC,
          );
          if (children?.length) {
            options = children.sort((c1, c2) => c1.name.localeCompare(c2.name));
          }
        }

        const sAttr: SecondaryAttribute = {
          id: s.transportationAttributeUUID!,
          name: s.name,
          controlType: options
            ? AttributeControlType.DROPDOWN
            : AttributeControlType.CHECKBOX,
          listOptions: options,
          displayOrder: s.displayOrder || 0,
        };
        if (
          s.subgrouping ===
          TransportationAttributeSubgrouping.ADDITIONALPASSENGER
        ) {
          passengerAttrs.push(sAttr);
        } else {
          otherAttrs.push(sAttr);
        }
      });
      passengerAttrs.sort((a, b) => a.displayOrder - b.displayOrder);
      setPassengerAttributes(passengerAttrs);
      otherAttrs.sort((a, b) => a.displayOrder - b.displayOrder);
      setOtherAttributes(otherAttrs);
    }
  }, [transportationAttributes]);

  const { watch } = formHook;
  const alternateTextee = watch(`AlternateContact`);
  const additionalPassengers = watch(`AdditionalPassengers`);

  const additionalPassengerChoices = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
  ];

  const [providerSearch, setProviderSearch] = useState<string>('');

  useEffect(() => {
    const filter = 'ctt0&active0'; // only get cleared and active providers
    const sortModel: SortModel = {
      column: 'transportationProviderName',
      direction: 'asc',
    };

    dispatch(
      providerSlice.fetchProviderListAsync({
        pageSize: 100,
        page: 1,
        filter,
        search: providerSearch,
        sortModel,
      }),
    );
  }, [providerSearch, dispatch]);

  const providers = useAppSelector(providerSlice.selectProviderList);

  const getProviderOptionLabel = (option: number) => {
    const provider = providers?.items?.find(
      (p) => p.transportationProviderID === option,
    );
    return provider?.transportationProviderName || '';
  };

  return (
    <StyledBox className={classes.container}>
      <PhoneInput
        name="MemberHomePhone"
        label={t('reservation.homePhone')}
        control={formHook.control}
        className={classes.phoneNumberFields}
      />
      <PhoneInput
        name="MemberMobilePhone"
        label={t('reservation.mobilePhone')}
        control={formHook.control}
        className={classes.phoneNumberFields}
      />
      {!isPortalReservation && (
        <>
          <Checkbox
            name="AllowText"
            label={t('reservation.allowText')}
            control={formHook.control}
          />
          <Divider />
        </>
      )}
      <ChipSelectMenu
        label={t('reservation.passengerNotes')}
        name={`PassengerNoteTypes`}
        values={passengerNoteTypes}
        valueAttr="tripLegPassengerNoteTypeBC"
        labelAttr="tripLegPassengerNoteTypeName"
        required={false}
        control={formHook.control}
      />
      <ChipSelectMenu
        label={t('reservation.driverNotes')}
        name={`DriverNoteTypes`}
        values={driverNoteTypes}
        valueAttr="tripLegDriverNoteTypeBC"
        labelAttr="tripLegDriverNoteTypeName"
        required={false}
        control={formHook.control}
      />
      <Checkbox
        name="ShowOtherInputs"
        label={t('reservation.otherInputs')}
        control={formHook.control}
      />
      {showOtherInputs && (
        <>
          {!isPortalReservation && (
            <>
              <Divider />
              <Checkbox
                name={`AlternateContact`}
                label={t('reservation.alternateTextee')}
                control={formHook.control}
              />
              {alternateTextee && (
                <>
                  <SelectMenu
                    className={classes.subctrl}
                    label={t('reservation.alternateContactType')}
                    name={`AlternateContactType`}
                    values={alternateContactTypes}
                    valueAttr="alternateContactTypeBC"
                    labelAttr="alternateContactTypeName"
                    required={true}
                    control={formHook.control}
                    errorMessage={
                      formHook.formState.errors.AlternateContactType
                        ?.message as string
                    }
                    rules={{ required: requiredFieldMessage }}
                  />
                  <Box className={classes.names}>
                    <TextInput
                      className={classes.name}
                      name={`AlternateContactFirstName`}
                      label={t('reservation.alternateTexteeFirstName')}
                      control={formHook.control}
                      required={true}
                      rules={{ required: requiredFieldMessage }}
                    />
                    <TextInput
                      className={classes.name}
                      name={`AlternateContactLastName`}
                      label={t('reservation.alternateTexteeLastName')}
                      control={formHook.control}
                      required={true}
                      rules={{ required: requiredFieldMessage }}
                    />
                  </Box>
                  <PhoneInput
                    label={t('reservation.alternateTexteePhone')}
                    name={`AlternateContactPhone`}
                    required={true}
                    className={classes.subctrl}
                    control={formHook.control}
                    formHook={formHook}
                    errors={formHook.formState.errors}
                  />
                  <Checkbox
                    className={classes.subctrl}
                    name={`AlternateContactAllowSMS`}
                    label={t('reservation.allowSMS')}
                    control={formHook.control}
                  />
                </>
              )}
            </>
          )}
          <Checkbox
            name={`AdditionalPassengers`}
            label={t('reservation.additionalPassengers')}
            control={formHook.control}
          />
          {additionalPassengers && (
            <>
              <SelectMenu
                className={classes.subctrl}
                label={t('reservation.additionalPassengerCount')}
                name={`AdditionalPassengerCount`}
                values={additionalPassengerChoices}
                labelAttr="label"
                valueAttr="value"
                required={true}
                control={formHook.control}
                errorMessage={
                  formHook.formState.errors.AdditionalPassengerCount
                    ?.message as string
                }
              />
              {!!passengerAttributes.length &&
                passengerAttributes.map((sa) => {
                  if (sa.controlType === AttributeControlType.CHECKBOX) {
                    return (
                      <Checkbox
                        key={sa.id}
                        className={classes.subctrl}
                        name={`SecondaryAttribute_${sa.id}`}
                        label={sa.name}
                        control={formHook.control}
                      />
                    );
                  } else if (
                    sa.controlType === AttributeControlType.DROPDOWN &&
                    sa.listOptions?.length
                  ) {
                    return (
                      <SelectMenu
                        key={sa.id}
                        className={classes.subctrl}
                        label={sa.name}
                        name={`SecondaryAttribute_${sa.id}`}
                        values={[emptyAttribute, ...sa.listOptions]}
                        valueAttr="transportationAttributeUUID"
                        labelAttr="name"
                        control={formHook.control}
                      />
                    );
                  }
                })}
            </>
          )}
          {!isPortalReservation && !!otherAttributes?.length && (
            <>
              <Divider />{' '}
              <Checkbox
                name={`ShowOtherAttributes`}
                label={t('reservation.otherAttributes')}
                control={formHook.control}
              />
            </>
          )}
          {!isPortalReservation &&
            !!otherAttributes?.length &&
            showOtherAttributes &&
            otherAttributes.map((sa) => {
              if (sa.controlType === AttributeControlType.CHECKBOX) {
                return (
                  <Checkbox
                    key={sa.id}
                    className={classes.subctrl}
                    name={`SecondaryAttribute_${sa.id}`}
                    label={sa.name}
                    control={formHook.control}
                  />
                );
              } else if (
                sa.controlType === AttributeControlType.DROPDOWN &&
                sa.listOptions?.length
              ) {
                return (
                  <SelectMenu
                    key={sa.id}
                    className={classes.subctrl}
                    label={sa.name}
                    name={`SecondaryAttribute_${sa.id}`}
                    values={[emptyAttribute, ...sa.listOptions]}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    control={formHook.control}
                  />
                );
              }
            })}
          {!isPortalReservation && (
            <>
              <Divider />
              <fieldset className={classes.attributes}>
                <legend>{t('reservation.requestProvider')}</legend>
                <Autocomplete
                  inputValue={providerSearch}
                  onInputChange={(_evt, val) => setProviderSearch(val)}
                  options={
                    providers?.items?.map(
                      (item) => item.transportationProviderID,
                    ) || []
                  }
                  getOptionLabel={getProviderOptionLabel}
                  getOptionKey={(key) => key}
                  onChange={(_, val: any) => {
                    formHook.setValue(`RequestedTransportationProviderID`, val);
                  }}
                  value={
                    formHook.getValues('RequestedTransportationProviderID') ||
                    null
                  }
                  includeInputInList
                  filterSelectedOptions
                  popupIcon={<ExpandMoreRounded />}
                  noOptionsText={
                    providerSearch
                      ? t('common.noProviders')
                      : t('common.typeToSearchProvider')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('reservation.provider')}
                      variant="outlined"
                    />
                  )}
                />
              </fieldset>
            </>
          )}
        </>
      )}
    </StyledBox>
  );
}
